// services/audioOverviewParsing.ts
//
// Routes, errors and tolerant response parsers for the Audio Overviews API
// (ORA-735). The API calls and the route contract are in `audioOverviews.ts`,
// which re-exports the public names below; import them from there.

import {
  AUDIO_OVERVIEW_FORMATS,
  AUDIO_OVERVIEW_LENGTHS,
  AUDIO_OVERVIEW_STATUSES,
  type AudioOverviewConfig,
  type AudioOverviewFormat,
  type AudioOverviewLength,
  type AudioOverviewScriptSegment,
  type AudioOverviewScriptTiming,
  type AudioOverviewStatus,
  type AudioOverviewSummary,
  type ArticleAudioOverviewBlockedBy,
  type ArticleAudioOverviewResponse,
} from '@/types/AudioOverview';

export const AUDIO_OVERVIEWS_ROUTE = '/api/v5/audio-overviews';

export const audioOverviewRoute = (id: string, suffix = ''): string =>
  `${AUDIO_OVERVIEWS_ROUTE}/${encodeURIComponent(id)}${suffix}`;

export const articleAudioOverviewRoute = (articleId: string): string =>
  `${AUDIO_OVERVIEWS_ROUTE}/articles/${encodeURIComponent(articleId)}`;

export const lectureAudioOverviewRoute = (lectureId: string): string =>
  `${AUDIO_OVERVIEWS_ROUTE}/lectures/${encodeURIComponent(lectureId)}`;

export type AudioOverviewErrorCode =
  | 'AUDIO_OVERVIEW_UPGRADE_REQUIRED'
  | 'AUDIO_OVERVIEW_DAILY_LIMIT'
  | 'AUDIO_OVERVIEW_IN_PROGRESS_LIMIT'
  | 'AUDIO_OVERVIEW_INVALID_LECTURES'
  | 'AUDIO_OVERVIEW_NO_SOURCE_CONTENT'
  | 'AUDIO_OVERVIEW_UNAVAILABLE'
  | 'AUDIO_OVERVIEW_NOT_READY'
  /** 409 on retry: this failure cannot be retried; create a new one. */
  | 'AUDIO_OVERVIEW_NOT_RETRYABLE'
  /** 400: the body failed validation. */
  | 'AUDIO_OVERVIEW_INVALID_REQUEST'
  /**
   * 409 on create: a live episode for this exact lecture set exists (ORA-810).
   * Thrown as `AudioOverviewAlreadyExistsError`, which carries it.
   */
  | 'AUDIO_OVERVIEW_ALREADY_EXISTS'
  | 'LOCKED'
  | 'ARTICLE_AUDIO_DAILY_LIMIT'
  | 'ARTICLE_AUDIO_IN_PROGRESS_LIMIT'
  /** A bare 404: the feature is off for the caller, or the item is not theirs. */
  | 'NOT_FOUND'
  | 'UNKNOWN';

const KNOWN_CODES = new Set<AudioOverviewErrorCode>([
  'AUDIO_OVERVIEW_UPGRADE_REQUIRED',
  'AUDIO_OVERVIEW_DAILY_LIMIT',
  'AUDIO_OVERVIEW_IN_PROGRESS_LIMIT',
  'AUDIO_OVERVIEW_INVALID_LECTURES',
  'AUDIO_OVERVIEW_NO_SOURCE_CONTENT',
  'AUDIO_OVERVIEW_UNAVAILABLE',
  'AUDIO_OVERVIEW_NOT_READY',
  'AUDIO_OVERVIEW_NOT_RETRYABLE',
  'AUDIO_OVERVIEW_INVALID_REQUEST',
  'AUDIO_OVERVIEW_ALREADY_EXISTS',
  'LOCKED',
  'ARTICLE_AUDIO_DAILY_LIMIT',
  'ARTICLE_AUDIO_IN_PROGRESS_LIMIT',
]);

/** Which daily cap a 429 DAILY_LIMIT hit: new overviews, or generation runs (retries included). */
export type AudioOverviewDailyLimitReason = 'CREATES' | 'RUNS';

export interface AudioOverviewErrorDetails {
  limit?: number;
  resetsAt?: string;
  maxInProgress?: number;
  reason?: AudioOverviewDailyLimitReason;
}

export class AudioOverviewApiError extends Error {
  readonly code: AudioOverviewErrorCode;
  readonly status: number | null;
  readonly details: AudioOverviewErrorDetails;

  constructor(
    code: AudioOverviewErrorCode,
    status: number | null,
    message: string,
    details: AudioOverviewErrorDetails = {}
  ) {
    super(message);
    this.name = 'AudioOverviewApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * A 409 on create (ORA-810): the caller already has a live episode for this
 * exact lecture set. `overview` is that episode, so the UI can show it
 * without another request.
 */
export class AudioOverviewAlreadyExistsError extends AudioOverviewApiError {
  readonly overview: AudioOverviewSummary;

  constructor(message: string, overview: AudioOverviewSummary) {
    super('AUDIO_OVERVIEW_ALREADY_EXISTS', 409, message);
    this.name = 'AudioOverviewAlreadyExistsError';
    this.overview = overview;
  }
}

export const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const asString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

export const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Normalises any rejection into an `AudioOverviewApiError`. */
export const toAudioOverviewError = (error: unknown): AudioOverviewApiError => {
  if (error instanceof AudioOverviewApiError) return error;

  const response = asRecord(asRecord(error)?.response);
  const status = asNumber(response?.status);
  const body = asRecord(response?.data);
  const rawCode = asString(body?.code);

  const message =
    asString(body?.message) ??
    (error instanceof Error ? error.message : 'Request failed');

  const details: AudioOverviewErrorDetails = {};
  const limit = asNumber(body?.limit);
  const resetsAt = asString(body?.resetsAt);
  const maxInProgress = asNumber(body?.maxInProgress);
  if (limit !== null) details.limit = limit;
  if (resetsAt !== null) details.resetsAt = resetsAt;
  if (maxInProgress !== null) details.maxInProgress = maxInProgress;
  if (body?.reason === 'CREATES' || body?.reason === 'RUNS') {
    details.reason = body.reason;
  }

  if (rawCode === 'AUDIO_OVERVIEW_ALREADY_EXISTS') {
    // Without a usable episode it is still ALREADY_EXISTS, just not showable.
    const overview = parseAudioOverviewSummary(body?.overview);
    if (overview) return new AudioOverviewAlreadyExistsError(message, overview);
  }

  if (rawCode && KNOWN_CODES.has(rawCode as AudioOverviewErrorCode)) {
    return new AudioOverviewApiError(
      rawCode as AudioOverviewErrorCode,
      status,
      message,
      details
    );
  }

  if (status === 404) {
    return new AudioOverviewApiError('NOT_FOUND', status, message, details);
  }

  return new AudioOverviewApiError('UNKNOWN', status, message, details);
};

export const isOneOf = <T extends string>(
  values: readonly T[],
  value: unknown
): value is T => typeof value === 'string' && values.includes(value as T);

/** One summary from the wire, or null when it cannot be trusted. */
export const parseAudioOverviewSummary = (
  raw: unknown
): AudioOverviewSummary | null => {
  const record = asRecord(raw);
  const id = asString(record?.id);
  if (!record || !id) return null;
  if (!isOneOf<AudioOverviewStatus>(AUDIO_OVERVIEW_STATUSES, record.status)) {
    return null;
  }

  const format: AudioOverviewFormat = isOneOf(
    AUDIO_OVERVIEW_FORMATS,
    record.format
  )
    ? record.format
    : 'DEEP_DIVE';

  const length: AudioOverviewLength = isOneOf(
    AUDIO_OVERVIEW_LENGTHS,
    record.length
  )
    ? record.length
    : 'DEFAULT';

  const lectures = Array.isArray(record.lectures)
    ? record.lectures.flatMap((lecture) => {
        const entry = asRecord(lecture);
        const lectureId = asString(entry?.id);
        if (!lectureId) return [];
        return [{ id: lectureId, title: asString(entry?.title) ?? '' }];
      })
    : [];

  const progress = asNumber(record.progress) ?? 0;
  const source = record.source === 'article' ? 'article' : 'lecture';

  return {
    id,
    source,
    shared: record.shared === true || source === 'article',
    articleId: asString(record.articleId),
    articleTitle: asString(record.articleTitle),
    title: asString(record.title),
    format,
    length,
    focusNotes: asString(record.focusNotes),
    status: record.status,
    progress: Math.min(100, Math.max(0, progress)),
    durationSec: asNumber(record.durationSec),
    lectures,
    createdAt: asString(record.createdAt) ?? new Date(0).toISOString(),
    readyAt: asString(record.readyAt),
    errorCode: asString(record.errorCode),
    positionSec: asNumber(record.positionSec),
    completedAt: asString(record.completedAt),
    locked: record.locked === true,
    retryable:
      typeof record.retryable === 'boolean'
        ? record.retryable
        : record.status === 'FAILED',
  };
};

const ARTICLE_BLOCKED_BY = new Set<ArticleAudioOverviewBlockedBy>([
  'locked',
  'daily_limit',
  'in_progress_limit',
  'article_daily_limit',
  'article_in_progress_limit',
]);

export const parseArticleAudioOverviewResponse = (
  raw: unknown
): ArticleAudioOverviewResponse => {
  const record = asRecord(raw) ?? {};
  const blockedBy = asString(record.blockedBy);

  return {
    overview: parseAudioOverviewSummary(record.overview),
    locked: record.locked === true,
    canGenerate: record.canGenerate === true,
    blockedBy:
      blockedBy &&
      ARTICLE_BLOCKED_BY.has(blockedBy as ArticleAudioOverviewBlockedBy)
        ? (blockedBy as ArticleAudioOverviewBlockedBy)
        : null,
  };
};

export const requireSummary = (raw: unknown): AudioOverviewSummary => {
  const summary = parseAudioOverviewSummary(raw);
  if (!summary) {
    throw new AudioOverviewApiError(
      'UNKNOWN',
      null,
      'Malformed audio overview response'
    );
  }
  return summary;
};

/**
 * The create route's hard ceiling on `lectureIds` (its zod schema). The
 * per-account `maxLectures` from `/config` is normally lower (10); it is
 * clamped here so the form can never allow a selection the route rejects.
 */
export const AUDIO_OVERVIEW_MAX_LECTURE_IDS = 50;

export const parseConfig = (raw: unknown): AudioOverviewConfig => {
  const record = asRecord(raw) ?? {};
  const locked = record.locked === true;

  return {
    canGenerate: record.canGenerate === true,
    locked,
    dailyLimit: asNumber(record.dailyLimit) ?? 0,
    usedToday: asNumber(record.usedToday) ?? 0,
    inProgress: asNumber(record.inProgress) ?? 0,
    maxInProgress: asNumber(record.maxInProgress) ?? 0,
    maxLectures: Math.min(
      asNumber(record.maxLectures) ?? 10,
      AUDIO_OVERVIEW_MAX_LECTURE_IDS
    ),
    maxFocusChars: asNumber(record.maxFocusChars) ?? 500,
    resetsAt: asString(record.resetsAt) ?? '',
  };
};

export const parseScriptTiming = (
  raw: unknown
): AudioOverviewScriptTiming[] | null => {
  if (!Array.isArray(raw)) return null;

  const timing = raw.flatMap((entry) => {
    const record = asRecord(entry);
    const turnStart = asNumber(record?.turnStart);
    const turnEnd = asNumber(record?.turnEnd);
    const startSec = asNumber(record?.startSec);
    const endSec = asNumber(record?.endSec);
    if (
      turnStart === null ||
      turnEnd === null ||
      startSec === null ||
      endSec === null ||
      turnEnd < turnStart
    ) {
      return [];
    }
    return [{ turnStart, turnEnd, startSec, endSec }];
  });

  return timing.length > 0
    ? timing.sort((left, right) => left.startSec - right.startSec)
    : null;
};

export const parseScriptSegments = (
  raw: unknown,
  hasTiming: boolean
): AudioOverviewScriptSegment[] => {
  if (!Array.isArray(raw)) return [];
  let nextIndex = 0;

  return raw.flatMap((entry) => {
    const segment = asRecord(entry);
    if (!segment || !Array.isArray(segment.turns)) return [];

    const turns = segment.turns.flatMap((rawTurn) => {
      const turn = asRecord(rawTurn);
      const text = asString(turn?.text)?.trim();
      if (!turn || !text) return [];

      // The server numbers turns over the whole script; count along if an
      // index is missing so the order still holds.
      const index = asNumber(turn.index) ?? nextIndex;
      nextIndex = index + 1;

      const factIds = Array.isArray(turn.factIds)
        ? turn.factIds.filter(
            (id): id is string => typeof id === 'string' && id.includes(':')
          )
        : [];

      return [
        {
          index,
          speaker: turn.speaker === 'B' ? ('B' as const) : ('A' as const),
          text,
          factIds,
          // A start time means nothing without timing to seek in.
          startSec: hasTiming ? asNumber(turn.startSec) : null,
        },
      ];
    });

    return turns.length > 0
      ? [{ heading: asString(segment.heading) ?? '', turns }]
      : [];
  });
};

export const parseScriptLectures = (raw: unknown): Record<string, string> => {
  const lectures: Record<string, string> = {};
  for (const [documentModelId, lectureId] of Object.entries(
    asRecord(raw) ?? {}
  )) {
    if (typeof lectureId === 'string' && lectureId) {
      lectures[documentModelId] = lectureId;
    }
  }
  return lectures;
};
